<script setup lang="ts">
// eslint-disable-next-line @typescript-eslint/no-explicit-any

import { useField, useForm } from 'vee-validate'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import * as yup from 'yup'
import {
  type CreateInternationalAccountPayload,
  createInternationalAccountAction,
  createInternationalPaypalAccountAction,
  createInternationalRevolutAccountAction,
  createIranianAccountAction,
  createIranianSrcAccountAction,
} from '@/api/functions/bank-account/create-bank-account.action'
import { getUserDetailAction } from '@/api/functions/users/get-user-detail.action'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import ErrorIcon from '/svg/error-icon.svg'

const props = defineProps<{
  modelValue?: boolean
  countries: Array<{ label: string; value: string | number }>
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const router = useRouter()
const toast = useToast()
const route = useRoute()

const userId = route.params.id as string

const isOpen = computed({
  get: () => props.modelValue ?? false,
  set: (value) => emit('update:modelValue', value),
})

const accountType = ref('بین المللی')
const internationalType = ref('بین بانکی')
const loading = ref(false)

const { data: userDetail } = useQueryApi({
  queryKey: ['user-detail', userId],
  queryFn: () => getUserDetailAction(userId),
  enabled: !!userId,
})

const userData = computed(() => ({
  detail: userDetail.value?.data,
}))

// Options

const accountTypeOptions = [
  { label: 'ارزی', value: 'بین المللی' },
  { label: 'ریالی', value: 'مقصد ریالی' },
]

const internationalTypeOptions = [
  { label: 'بین بانکی', value: 'بین بانکی' },
  { label: 'رولوت', value: 'رولوت' },
  { label: 'پی پال', value: 'پی پال' },
]

const poundOptions = [
  { label: 'بله', value: true },
  { label: 'خیر', value: false },
]

// Validation Schema

const createValidationSchema = () => {
  const baseInternationalSchema = {
    bankName: yup
      .string()
      .required('نام بانک الزامی است')
      .min(2, 'نام بانک باید حداقل ۲ کاراکتر باشد')
      .max(100, 'نام بانک باید حداکثر ۱۰۰ کاراکتر باشد')
      .matches(/^[a-zA-Z ]*$/, 'نام بانک باید فقط حروف انگلیسی باشد'),

    ownerName: yup
      .string()
      .required('نام دارنده حساب الزامی است')
      .min(3, 'نام دارنده حساب باید حداقل ۳ کاراکتر باشد')
      .max(100, 'نام دارنده حساب باید حداکثر ۱۰۰ کاراکتر باشد')
      .matches(/^[a-zA-Z ]*$/, 'نام دارنده حساب باید فقط حروف انگلیسی باشد'),

    country: yup.string().required('کشور الزامی است'),
  }

  const baseDomesticSchema = {
    sheba: yup
      .string()
      .required('شماره شبا الزامی است')
      .matches(/^[0-9]{24}$/, 'شماره شبا باید ۲۴ رقم باشد'),

    cardNumber: yup
      .string()
      .required('شماره کارت الزامی است')
      .length(16, 'شماره کارت باید ۱۶ رقمی باشد')
      .matches(/^[0-9]+$/, 'شماره کارت باید فقط عدد باشد'),

    bankName: yup
      .string()
      .required('نام بانک الزامی است')
      .min(2, 'نام بانک باید حداقل ۲ کاراکتر باشد')
      .max(100, 'نام بانک باید حداکثر ۱۰۰ کاراکتر باشد'),

    ownerName: yup
      .string()
      .required('نام دارنده حساب الزامی است')
      .min(3, 'نام دارنده حساب باید حداقل ۳ کاراکتر باشد')
      .max(100, 'نام دارنده حساب حداکثر ۱۰۰ کاراکتر باشد'),

    // nationalCode: yup
    //     .string()
    //     .required('کد ملی الزامی است')
    //     .length(
    //         10,
    //         'کد ملی باید ۱۰ رقمی باشد'
    //     )
    //     .matches(
    //         /^[0-9]+$/,
    //         'کد ملی باید فقط عدد باشد'
    //     ),
  }

  if (accountType.value === 'بین المللی') {
    if (internationalType.value === 'بین بانکی') {
      return yup.object({
        ...baseInternationalSchema,

        iban: yup
          .string()
          .required('شماره IBAN الزامی است')
          .transform((value) => value.replace(/\s|-/g, '').toUpperCase())
          .matches(/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/, 'فرمت IBAN معتبر نیست')
          .min(15, 'شماره IBAN باید حداقل ۱۵ کاراکتر باشد')
          .max(34, 'شماره IBAN باید حداکثر ۳۴ کاراکتر باشد'),

        bic: yup
          .string()
          .required('شماره BIC الزامی است')
          .min(8, 'شماره BIC باید حداقل ۸ کاراکتر باشد')
          .max(11, 'شماره BIC باید حداکثر ۱۱ کاراکتر باشد'),

        isPound: yup.boolean().nullable(),

        sortCode: yup.string().when('isPound', {
          is: true,
          then: (schema) => schema.required('شماره Sort Code برای حساب پوند الزامی است'),
          otherwise: (schema) => schema.notRequired(),
        }),

        accountNumber: yup.string().when('isPound', {
          is: true,
          then: (schema) => schema.required('شماره Account Number برای حساب پوند الزامی است'),
          otherwise: (schema) => schema.notRequired(),
        }),
      })
    }

    if (internationalType.value === 'رولوت') {
      return yup.object({
        ...baseInternationalSchema,

        iban: yup
          .string()
          .required('شماره IBAN الزامی است')
          .transform((value) => value.replace(/\s|-/g, '').toUpperCase())
          .matches(/^[A-Z]{2}\d{2}[A-Z0-9]{1,30}$/, 'فرمت IBAN معتبر نیست')
          .min(15, 'شماره IBAN باید حداقل ۱۵ کاراکتر باشد')
          .max(34, 'شماره IBAN باید حداکثر ۳۴ کاراکتر باشد'),

        bic: yup
          .string()
          .required('شماره BIC الزامی است')
          .min(8, 'شماره BIC باید حداقل ۸ کاراکتر باشد')
          .max(11, 'شماره BIC باید حداکثر ۱۱ کاراکتر باشد'),

        revolutTag: yup
          .string()
          .required('Revolut Tag الزامی است')
          .min(2, 'Revolut Tag باید حداقل ۲ کاراکتر باشد')
          .max(50, 'Revolut Tag باید حداکثر ۵۰ کاراکتر باشد'),
      })
    }

    if (internationalType.value === 'پی پال') {
      return yup.object({
        ...baseInternationalSchema,

        paypalEmail: yup.string().required('ایمیل Paypal الزامی است').email('فرمت ایمیل معتبر نیست'),

        paypalId: yup
          .string()
          .required('ایمیل Paypal الزامی است')
          .nullable()
          .max(50, 'Paypal ID باید حداکثر ۵۰ کاراکتر باشد'),
      })
    }
  }

  if (accountType.value === 'مقصد ریالی') {
    const domesticSchema = {
      ...baseDomesticSchema,
    }

    return yup.object({
      ...domesticSchema,

      birthDate: yup.string().required('تاریخ تولد الزامی است'),
    })
  }

  return yup.object({})
}

// Validation Schema

const validationSchema = computed(() => createValidationSchema())

// Form setup

const { handleSubmit, resetForm, errors } = useForm({
  validationSchema,

  initialValues: {
    isPound: null,

    country: '',

    birthDate: '',

    paypalEmail: '',
    paypalId: '',

    iban: '',
    bic: '',

    bankName: '',
    ownerName: '',

    sortCode: '',
    accountNumber: '',

    revolutTag: '',

    sheba: '',
    cardNumber: '',
    // nationalCode: '',
  },
})

// Fields - International

const { value: paypalEmail } = useField('paypalEmail')

const { value: paypalId } = useField('paypalId')

const { value: iban } = useField('iban')

const { value: bic } = useField('bic')

const { value: bankName } = useField('bankName')

const { value: ownerName } = useField('ownerName')

const { value: country } = useField('country')

const { value: isPound } = useField('isPound')

const { value: sortCode } = useField('sortCode')

const { value: accountNumber } = useField('accountNumber')

const { value: revolutTag } = useField('revolutTag')

// Fields - Domestic

const { value: sheba } = useField('sheba')

const { value: cardNumber } = useField('cardNumber')

// const { value: nationalCode } =
//     useField('nationalCode')

const { value: birthDate } = useField<string>('birthDate')

// Computed fields for dynamic labels

const ibanField = computed({
  get: () => (internationalType.value === 'پی پال' ? paypalEmail.value : iban.value),

  set: (value) => {
    if (internationalType.value === 'پی پال') {
      paypalEmail.value = value
    } else {
      iban.value = value
    }
  },
})

const bicField = computed({
  get: () => (internationalType.value === 'پی پال' ? paypalId.value : bic.value),

  set: (value) => {
    if (internationalType.value === 'پی پال') {
      paypalId.value = value
    } else {
      bic.value = value
    }
  },
})

// Methods

const onAccountTypeChange = () => {
  internationalType.value = accountType.value === 'بین المللی' ? 'بین بانکی' : ''

  resetForm()
}

const onInternationalTypeChange = () => {
  resetForm()
}

// Mutation for international accounts

const { mutate: createInternationalAccount } = useMutationApi({
  mutationKey: ['createInternationalAccount'],

  mutationFn: createInternationalAccountAction,

  onSuccess: () => {
    loading.value = false

    toast.success('حساب بین‌المللی با موفقیت ایجاد شد', 'موفقیت')

    router.push(`/users/${userId}/accounts`)
  },

  onError: (error: Error) => {
    loading.value = false

    toast.error(getErrorMessage(error), 'خطا')
  },
})

// Mutation for international revolut accounts

const { mutate: createInternationalRevolutAccount } = useMutationApi({
  mutationKey: ['createInternationalRevolutAccount'],

  mutationFn: createInternationalRevolutAccountAction,

  onSuccess: () => {
    loading.value = false

    toast.success('حساب رولوت با موفقیت ایجاد شد', 'موفقیت')

    router.push(`/users/${userId}/accounts`)
  },

  onError: (error: Error) => {
    loading.value = false

    toast.error(getErrorMessage(error), 'خطا')
  },
})

// Mutation for international paypal accounts

const { mutate: createInternationalPaypalAccount } = useMutationApi({
  mutationKey: ['createInternationalPaypalAccount'],

  mutationFn: createInternationalPaypalAccountAction,

  onSuccess: () => {
    loading.value = false

    toast.success('حساب پی پال با موفقیت ایجاد شد', 'موفقیت')

    router.push(`/users/${userId}/accounts`)
  },

  onError: (error: Error) => {
    loading.value = false

    toast.error(getErrorMessage(error), 'خطا')
  },
})

// Mutation for iranian accounts

const { mutate: createIranianAccount } = useMutationApi({
  mutationKey: ['createIranianAccount'],

  mutationFn: createIranianAccountAction,

  onSuccess: () => {
    loading.value = false

    toast.success('حساب مقصد ریالی با موفقیت ایجاد شد', 'موفقیت')

    router.push(`/users/${userId}/accounts`)
  },

  onError: (error: Error) => {
    loading.value = false

    toast.error(getErrorMessage(error), 'خطا')
  },
})

// Mutation for iranian source accounts

const { mutate: createIranianSrcAccount } = useMutationApi({
  mutationKey: ['createIranianSrcAccount'],

  mutationFn: createIranianSrcAccountAction,

  onSuccess: () => {
    loading.value = false

    toast.success('حساب مبداء ریالی با موفقیت ایجاد شد', 'موفقیت')

    router.push(`/users/${userId}/accounts`)
  },

  onError: (error: Error) => {
    loading.value = false

    toast.error(getErrorMessage(error), 'خطا')
  },
})

// Submit

const submitForm = handleSubmit((values: unknown) => {
  loading.value = true

  try {
    if (!userData?.value?.detail?.id) {
      loading.value = false
      return
    }

    const formValues = values as Record<string, any>

    switch (accountType.value) {
      case 'بین المللی':
        switch (internationalType.value) {
          case 'بین بانکی': {
            const bankTransferPayload: {
              userId: string
            } & CreateInternationalAccountPayload = {
              userId: userData.value.detail.id,

              iban_number: formValues.iban,

              bic_number: formValues.bic,

              bank_name: formValues.bankName,

              owner_full_name: formValues.ownerName,

              country_id: formValues.country,
            }

            if (formValues.isPound) {
              bankTransferPayload.sort_code = formValues.sortCode

              bankTransferPayload.account_number = formValues.accountNumber

              bankTransferPayload.is_pound = true
            }

            createInternationalAccount(bankTransferPayload)

            break
          }

          case 'رولوت':
            createInternationalRevolutAccount({
              userId: userData.value.detail.id,

              iban_number: formValues.iban,

              bic_number: formValues.bic,

              bank_name: formValues.bankName,

              owner_full_name: formValues.ownerName,

              country_id: formValues.country,

              revolut_tag: formValues.revolutTag,
            })

            break

          case 'پی پال':
            createInternationalPaypalAccount({
              userId: userData.value.detail.id,

              paypal_email: formValues.paypalEmail,

              paypal_id: formValues.paypalId || undefined,

              bank_name: formValues.bankName,

              owner_full_name: formValues.ownerName,

              country_id: formValues.country,
            })

            break
        }

        break

      case 'مقصد ریالی':
      case 'مبداء ریالی': {
        const shebaWithIR = `IR${formValues.sheba}`

        if (accountType.value === 'مقصد ریالی') {
          createIranianAccount({
            userId: userData?.value?.detail?.id,

            sheba_number: shebaWithIR,

            card_number: formValues.cardNumber,

            bank_name: formValues.bankName,

            owner_full_name: formValues.ownerName,

            national_code: formValues.nationalCode,

            birth_date: formValues.birthDate,
            // TODO: backend
          })
        } else {
          createIranianSrcAccount({
            userId: userData?.value?.detail?.id,

            sheba_number: shebaWithIR,

            card_number: formValues.cardNumber,

            bank_name: formValues.bankName,

            owner_full_name: formValues.ownerName,

            national_code: formValues.nationalCode,

            birth_date: formValues.birthDate,
            // TODO: backend
          })
        }

        break
      }
    }
  } catch (error) {
    loading.value = false

    toast.error(getErrorMessage(error), 'خطا')
  }
})

const filterEnglishCharacters = (event: Event) => {
  const input = event.target as HTMLInputElement

  const englishOnlyRegex = /^[a-zA-Z ]*$/

  if (!englishOnlyRegex.test(input.value)) {
    input.value = input.value.replace(/[^a-zA-Z ]/g, '')
  }
}
</script>

<template>
    <OffCanvasPanel v-model="isOpen" width="400px" mobile-height="100dvh">
        <template #header>
            <span class="font-medium text-base text-[#0F1114]">
                حساب جدید
            </span>
        </template>

        <form id="create-bank-account-form" @click.stop @submit="submitForm" class="flex flex-col gap-6 px-8">
            <!-- Account Type -->

            <div class="flex gap-6">
                <span class="text-sm font-medium text-[#1A1A1A]">
                    مبدا حساب
                </span>

                <Radio v-model="accountType" name="accountType" :options="accountTypeOptions"
                    @update:modelValue="onAccountTypeChange" />
            </div>

            <!-- International Type -->

            <div v-if="accountType === 'بین المللی'" class="flex gap-6">
                <span class="text-sm font-medium text-[#1A1A1A]">
                    نوع حساب
                </span>

                <Radio v-model="internationalType" name="internationalType" :options="internationalTypeOptions"
                    @update:modelValue="onInternationalTypeChange" />
            </div>

            <!-- Info Banner -->

            <div v-if="
                accountType === 'بین المللی' &&
                internationalType === 'پی پال'
            "
                class="flex items-center gap-2 text-xs border p-2 text-[#1A1A1A] rounded-full border-[#97C1FC] bg-[#E8F5FF]">
                <img src="/svg/ic-info.d746bec-black.svg" />

                لطفا به هیچ وجه از حساب بیزنسی استفاده نکنید.
            </div>

            <!-- Form Fields -->

            <div v-if="
                accountType &&
                (
                    accountType !== 'بین المللی' ||
                    internationalType
                )
            " class="flex flex-col gap-6">
                <!-- International -->

                <div v-if="accountType === 'بین المللی'" class="flex flex-col gap-2">

                    <div v-if="internationalType === 'بین بانکی'" class="flex gap-6 mb-4">
                        <span class="text-sm font-medium text-[#1A1A1A]">
                            آیا حساب شما پوند است؟
                        </span>

                        <Radio v-model="isPound" name="isPound" :options="poundOptions" />
                    </div>


                    <!-- PayPal -->

                    <div v-if="internationalType === 'پی پال'" class="flex flex-col gap-2">
                        <div>
                            <Input v-model="ibanField" name="ibanField" appearance="admin" label="PayPal Email"
                                type="email" placeholder="example@paypal.com" />

                            <div v-if="errors['paypalEmail']"
                                class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                                <img :src="ErrorIcon" width="15" alt="error-icon" />

                                <span>
                                    {{ errors['paypalEmail'] }}
                                </span>
                            </div>
                        </div>

                        <div>
                            <Input v-model="bicField" name="bicField" :isTag="true" appearance="admin" label="Paypal Id"
                                type="text" placeholder="PID123456789" />

                            <div v-if="errors['paypalId']"
                                class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                                <img :src="ErrorIcon" width="15" alt="error-icon" />

                                <span>
                                    {{ errors['paypalId'] }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- IBAN / BIC -->

                    <div v-if="
                        internationalType === 'بین بانکی' ||
                        internationalType === 'رولوت'
                    " class="flex flex-col gap-2">
                        <div>
                            <Input v-model="ibanField" name="ibanField" appearance="admin" label="شماره IBAN"
                                type="text" placeholder="IE84REVO99036020197146" />

                            <div v-if="errors['iban']"
                                class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                                <img :src="ErrorIcon" width="15" alt="error-icon" />

                                <span>
                                    {{ errors['iban'] }}
                                </span>
                            </div>
                        </div>

                        <div>
                            <Input v-model="bicField" name="bicField" appearance="admin" label="شماره BIC CODE"
                                type="text" placeholder="REVOIE2D" />

                            <div v-if="errors['bic']"
                                class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                                <img :src="ErrorIcon" width="15" alt="error-icon" />

                                <span>
                                    {{ errors['bic'] }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Common International -->

                    <div class="flex flex-col gap-2">
                        <div>
                            <Input v-model="bankName" name="bankName" appearance="admin"
                                @input="filterEnglishCharacters" label="نام بانک  به انگلیسی  " type="text"
                                placeholder="Bank Name" />

                            <div v-if="errors.bankName"
                                class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                                <img :src="ErrorIcon" width="15" alt="error-icon" />

                                <span>
                                    {{ errors.bankName }}
                                </span>
                            </div>
                        </div>

                        <div>
                            <Input v-model="ownerName" name="ownerName" appearance="admin"
                                @input="filterEnglishCharacters" label="نام صاحب حساب" type="text"
                                placeholder="Full Name" />

                            <div v-if="errors.ownerName"
                                class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                                <img :src="ErrorIcon" width="15" alt="error-icon" />

                                <span>
                                    {{ errors.ownerName }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Country / Pound -->

                    <div class="flex flex-col gap-2">
                        <div>
                            <CountrySelect v-model="country" name="country" appearance="admin" label="کشور مقصد" />

                            <div v-if="errors.country"
                                class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                                <img :src="ErrorIcon" width="15" alt="error-icon" />

                                <span>
                                    {{ errors.country }}
                                </span>
                            </div>
                        </div>

                        <!-- Pound Fields -->

                        <div v-if="
                            isPound === true &&
                            internationalType === 'بین بانکی'
                        " class="flex flex-col gap-2">
                            <div>
                                <Input v-model="accountNumber" name="accountNumber" appearance="admin"
                                    label="Account Number" type="text" placeholder="12345678" />

                                <div v-if="errors['accountNumber']"
                                    class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                                    <img :src="ErrorIcon" width="15" alt="error-icon" />

                                    <span>
                                        {{ errors['accountNumber'] }}
                                    </span>
                                </div>
                            </div>

                            <div>
                                <Input v-model="sortCode" appearance="admin" name="sortCode" label=" Sort Code   "
                                    type="text" placeholder="123456" />

                                <div v-if="errors['sortCode']"
                                    class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                                    <img :src="ErrorIcon" width="15" alt="error-icon" />

                                    <span>
                                        {{ errors['sortCode'] }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Revolut -->

                        <div v-if="internationalType === 'رولوت'">
                            <Input v-model="revolutTag" :isTag="true" appearance="admin" name="revolutTag"
                                label="Revolut Tag" type="text" placeholder="@username" />

                            <div v-if="errors['revolutTag']"
                                class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                                <img :src="ErrorIcon" width="15" alt="error-icon" />

                                <span>
                                    {{ errors['revolutTag'] }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Domestic -->

                <div v-if="
                    accountType === 'مقصد ریالی' ||
                    accountType === 'مبداء ریالی'
                " class="flex flex-col gap-2">
                    <!-- Sheba / Card -->

                    <div class="flex flex-col gap-2">
                        <div>
                            <Input v-model="sheba" name="sheba" :isSheba="true" appearance="admin" label="شماره شبا"
                                type="text" placeholder="060120000000009886055009" />

                            <div v-if="errors.sheba"
                                class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                                <img :src="ErrorIcon" width="15" alt="error-icon" />

                                <span>
                                    {{ errors.sheba }}
                                </span>
                            </div>
                        </div>

                        <div>
                            <Input v-model="cardNumber" name="cardNumber" appearance="admin" label="شماره کارت"
                                type="text" placeholder="6104338650180389" />

                            <div v-if="errors.cardNumber"
                                class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                                <img :src="ErrorIcon" width="15" alt="error-icon" />

                                <span>
                                    {{ errors.cardNumber }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Bank / Owner -->

                    <div class="flex flex-col gap-2">
                        <div>
                            <Input v-model="bankName" name="bankName" appearance="admin" label="نام بانک" type="text"
                                placeholder="ملی تست" />

                            <div v-if="errors.bankName"
                                class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                                <img :src="ErrorIcon" width="15" alt="error-icon" />

                                <span>
                                    {{ errors.bankName }}
                                </span>
                            </div>
                        </div>

                        <div>
                            <Input v-model="ownerName" name="ownerName" appearance="admin" label="نام صاحب حساب"
                                type="text" />

                            <div v-if="errors.ownerName"
                                class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                                <img :src="ErrorIcon" width="15" alt="error-icon" />

                                <span>
                                    {{ errors.ownerName }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- National Code -->
                    <!-- <div>
                        <Input v-model="nationalCode" name="nationalCode" label="کد ملی" type="text"
                           />

                        <div v-if="errors.nationalCode"
                            class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b] mt-1">
                            <img :src="ErrorIcon" width="15" alt="error-icon" />

                            <span>
                                {{ errors.nationalCode }}
                            </span>
                        </div>
                    </div> -->

                    <!-- Birth Date -->

                    <div class="flex flex-col gap-2">
                        <!-- Birth Date -->

                        <AlirezaDatePicker name="birthDate" v-model="birthDate" label="تاریخ تولد"
                            placeholder="تاریخ تولد" :error="errors.birthDate" appearance="admin" />

                        <div v-if="errors.birthDate"
                            class="flex items-center gap-1 text-[10px] font-bold text-[#ed145b]">
                            <img :src="ErrorIcon" width="15" alt="error-icon" />

                            <span>
                                {{ errors.birthDate }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </form>

        <template #footer>
            <div class="flex justify-end">
                <Button type="submit" form="create-bank-account-form" :disabled="loading" :isAdmin="true"
                    class="w-fit h-11 px-6!" size="sm">
                    <span>
                        {{
                            loading
                                ? 'در حال ثبت...'
                                : 'ایجاد حساب'
                        }}
                    </span>
                </Button>
            </div>
        </template>
    </OffCanvasPanel>
</template>

<style scoped></style>
